import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Alert,
  Snackbar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { collection, getDocs, query, where } from '../../services/firestoreCompat';
import { db } from '../../services/firebaseConfig';

interface DiscountEntry {
  id: string;
  order_id: string;
  date: string;
  order_number: string;
  discount_type: string;
  original_amount: number;
  discount_amount: number;
  final_amount: number;
  applied_by: string;
  reason: string;
}

const DiscountMonitoringPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<DiscountEntry[]>([]);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const loadDiscounts = useCallback(async () => {
    setLoading(true);
    const seenOrderIds = new Set<string>();
    const allEntries: DiscountEntry[] = [];

    // Load from discount_log collection
    try {
      const logSnap = await getDocs(collection(db, 'discount_log'));
      logSnap.forEach((d) => {
        const data = d.data();
        const orderId = data.order_id || d.id;
        if (!seenOrderIds.has(orderId)) {
          seenOrderIds.add(orderId);
          allEntries.push({
            id: d.id,
            order_id: orderId,
            date: data.date || data.created_at || data.timestamp || '',
            order_number: data.order_number || data.order_id || d.id,
            discount_type: data.discount_type || data.type || 'Manual',
            original_amount: data.original_amount || data.subtotal || 0,
            discount_amount: data.discount_amount || data.discount || 0,
            final_amount: data.final_amount || data.total || 0,
            applied_by: data.applied_by || data.staff || data.user || 'Unknown',
            reason: data.reason || data.note || '',
          });
        }
      });
    } catch (err) {
      console.warn('[DISCOUNTS] Failed to load discount_log:', err);
    }

    // Fallback: load from orders where discount_amount > 0
    try {
      const ordersSnap = await getDocs(collection(db, 'orders'));
      ordersSnap.forEach((d) => {
        const data = d.data();
        const discountAmt = data.discount_amount || data.discount || 0;
        if (discountAmt > 0) {
          const orderId = d.id;
          if (!seenOrderIds.has(orderId)) {
            seenOrderIds.add(orderId);
            allEntries.push({
              id: d.id,
              order_id: orderId,
              date: data.created_at || data.date || data.timestamp || '',
              order_number: data.order_number || data.display_id || d.id,
              discount_type: data.discount_type || 'Order Discount',
              original_amount: (data.subtotal || data.total || 0) + discountAmt,
              discount_amount: discountAmt,
              final_amount: data.total || data.final_total || 0,
              applied_by: data.discount_applied_by || data.staff_name || data.cashier || 'Unknown',
              reason: data.discount_reason || data.discount_note || '',
            });
          }
        }
      });
    } catch (err) {
      console.warn('[DISCOUNTS] Failed to load orders fallback:', err);
    }

    // Sort by date descending
    allEntries.sort((a, b) => {
      const da = new Date(a.date).getTime() || 0;
      const db2 = new Date(b.date).getTime() || 0;
      return db2 - da;
    });

    setEntries(allEntries);
    setTotalDiscount(allEntries.reduce((sum, e) => sum + e.discount_amount, 0));
    setLoading(false);
  }, []);

  useEffect(() => { loadDiscounts(); }, [loadDiscounts]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Skeleton width={300} height={36} sx={{ mb: 3 }} />
          <Skeleton height={60} sx={{ borderRadius: 2.5, mb: 2 }} />
          <Skeleton height={400} sx={{ borderRadius: 2.5 }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#e0e0e0', letterSpacing: '-0.02em' }}>
              Discount Monitoring
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
              Track all discounts applied to orders
            </Typography>
          </Box>
        </Box>

        {/* Summary Card */}
        <Paper sx={{ borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', mb: 3, p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, md: 4 }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Total Discounts Given</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f' }}>{formatCurrency(totalDiscount)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Number of Discounted Orders</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#e0e0e0' }}>{entries.length}</Typography>
          </Box>
          {entries.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Average Discount</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                {formatCurrency(totalDiscount / entries.length)}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Table */}
        <Paper sx={{ borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', overflow: 'hidden' }}>
          {entries.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: '#6b7280' }}>No discounts found</Typography>
              <Typography variant="body2" sx={{ color: '#bbb', mt: 1 }}>
                Discounts will appear here when they are applied to orders
              </Typography>
            </Box>
          ) : (
            <>
            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, p: 1.5 }}>
              {entries.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{
                    backgroundColor: '#252838',
                    border: '1px solid #3a3d52',
                    borderRadius: '10px',
                    p: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#fff', fontSize: '0.85rem' }}>
                        #{entry.order_number}
                      </Typography>
                      <Chip
                        label={entry.discount_type}
                        size="small"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: 'rgba(0, 200, 150,0.12)', color: '#00C896' }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                      {formatDate(entry.date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem', display: 'block' }}>Original</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>{formatCurrency(entry.original_amount)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem', display: 'block' }}>Discount</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#d32f2f' }}>-{formatCurrency(entry.discount_amount)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem', display: 'block' }}>Final</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#00C896' }}>{formatCurrency(entry.final_amount)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      by {entry.applied_by}
                    </Typography>
                    {entry.reason && (
                      <Typography variant="caption" sx={{ color: '#6b7280', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.reason}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Desktop Table View */}
            <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0, 200, 150,0.08)' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Order #</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Original</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Discount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Final</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Applied By</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 200, 150,0.08)' } }}>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>{formatDate(entry.date)}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {entry.order_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Chip
                          label={entry.discount_type}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#f0f0f0', color: '#9ca3af' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                          {formatCurrency(entry.original_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#d32f2f' }}>
                          -{formatCurrency(entry.discount_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#e0e0e0' }}>
                          {formatCurrency(entry.final_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>{entry.applied_by}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#9ca3af', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.reason || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </Paper>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default DiscountMonitoringPage;
